/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, May 2022
 * @format
 * @flow
 */

import CApiWebSocketClient from './CApiWebSocketClient';
import { dispatchUpdateSyncStatus } from '../store/sync-store';
import { dispatchRethinkDBLogs } from '../store';
import { LogLevelEnum, SyncStatus } from '../enums';
import EventEmitter from '../util/event-emitter';
import API from '../requests';
import FireboardUtils from '../util/fireboard-util';

class CApiWebSocketService {
  cApiWebSocketClient = null;
  isConnected = false;
  healthInfo = {
    storeId: 0,
    deviceTypeId: 0,
    deviceExternalId: null,
    connected: false,
    connectCount: 0,
    currentConnectionDurationInMilliseconds: 0,
    averageConnectionDurationInMilliseconds: 0,
    lastConnectDate: null,
    lastDisconnectDate: null,
    currentDate: null,
    lastError: null
  };

  constructor(storeId, deviceTypeId) {
    this.healthInfo.storeId = storeId;
    this.healthInfo.deviceTypeId = deviceTypeId;

    this.cApiWebSocketClient = new CApiWebSocketClient();
    this.cApiWebSocketClient.onConnected = this.onConnected;
    this.cApiWebSocketClient.onReconnecting = this.onReconnecting;
    this.cApiWebSocketClient.onReconnected = this.onReconnected;
    this.cApiWebSocketClient.onClose = this.onClose;
  }

  onConnected = () => {
    try {
      console.log('Connected');
      this.isConnected = true;
      this.subscribeToStoreChecks();
      this.subscribeToStoreNotifications();
      this.subscribeToStoreBookings();
      this.setSyncStatusToConnected();

      const previousConnectCount = this.healthInfo.connectCount;
      this.healthInfo.connected = true;
      this.healthInfo.connectCount++;
      this.healthInfo.lastConnectDate = new Date();
      if (previousConnectCount === 0) {
        this.sendHealthInfo(); // Send health info on first connect
      }
    } catch (e) {
      console.error('onConnected error', this.healthInfo, e);
    }
  };

  onReconnecting = error => {
    try {
      const errMessage = `Connection lost. Attempting to reconnect. Error:- ${error}`;
      console.warn(errMessage, 'Reconnecting...');
      this.setSyncStatusToDisconnected();

      const previousLastDisconnectDate = this.healthInfo.lastDisconnectDate;
      this.healthInfo.connected = false;
      this.healthInfo.lastDisconnectDate = new Date();
      this.healthInfo.lastError = `${error}`;

      if (this.healthInfo.lastConnectDate && this.healthInfo.lastDisconnectDate) {
        const lastConnectionDurationInMilliseconds = this.healthInfo.lastDisconnectDate - this.healthInfo.lastConnectDate;
        if (this.healthInfo.averageConnectionDurationInMilliseconds === 0) {
          this.healthInfo.averageConnectionDurationInMilliseconds = lastConnectionDurationInMilliseconds;
        } else {
          const newAverage = (this.healthInfo.averageConnectionDurationInMilliseconds + lastConnectionDurationInMilliseconds) / 2;
          this.healthInfo.averageConnectionDurationInMilliseconds = Math.round(newAverage);
        }
      }

      if (previousLastDisconnectDate === null) {
        this.sendHealthInfo(); // Send health info on first disconnect
      }
    } catch (e) {
      console.error('onReconnecting error', this.healthInfo, e);
    }
  };

  onReconnected = () => {
    try {
      console.log('Connection re-establised.');
      this.setSyncStatusToConnected();

      this.healthInfo.connected = true;
      this.healthInfo.connectCount++;
      this.healthInfo.lastConnectDate = new Date();
    } catch (e) {
      console.error('onReconnected error', this.healthInfo, e);
    }
  };

  onClose = error => {
    console.error('Client connection has been closed.', error);
  };

  connect = (baseUrl, apiKey, deviceExternalId, subscriber) => {
    console.log(baseUrl, apiKey, deviceExternalId);

    if (!baseUrl) {
      console.error('baseUrl is required');
      return;
    }

    if (!apiKey) {
      console.error('apiKey is required');
      return;
    }

    if (!deviceExternalId) {
      console.error('deviceExternalId is required');
      return;
    }

    this.cApiWebSocketClient.onMessageReceived = message => {
      console.log('Message Received:- ', message.messageType, message);
      subscriber && subscriber(message);
    };

    this.isConnected = false;
    this.healthInfo.deviceExternalId = deviceExternalId;
    setInterval(this.sendHealthInfo, 300000); // Every 5 minutes send health info

    this.cApiWebSocketClient.connect(baseUrl, apiKey, deviceExternalId);
  };

  subscribeToStoreChecks = () => {
    console.log('subscribing to store checks...');
    if (!this.isConnected) {
      console.error('WebSocket is not connected. Please connect first before subscribing');
      return;
    }

    this.cApiWebSocketClient.subscribeToStoreChecks()
      .then(data => {
        console.log('subscribeToStoreChecks response:- ', data);
      })
      .catch(err => {
        console.error('Error subscribing to store checks. Error:- ' + err);
      });
  };

  subscribeToStoreNotifications = () => {
    console.log('subscribing to store notifications...');
    if (!this.isConnected) {
      console.error('WebSocket is not connected. Please connect first before subscribing');
      return;
    }

    this.cApiWebSocketClient.subscribeToStoreNotifications()
      .then(data => {
        console.log('subscribeToStoreNotifications response:- ', data);
      })
      .catch(err => {
        console.error('Error subscribing to store checks. Error:- ' + err);
      });
  };

  subscribeToStoreBookings = () => {
    console.log('subscribing to store bookings...');
    if (!this.isConnected) {
      console.error('WebSocket is not connected. Please connect first before subscribing');
      return;
    }

    this.cApiWebSocketClient.subscribeToStoreBookings()
      .then(data => {
        console.log('subscribeToStoreBookings response:- ', data);
      })
      .catch(err => {
        console.error('Error subscribing to store bookings. Error:- ' + err);
      });
  };

  emitSyncLogs = (logLevel, logMessage) => {
    if (window.ReactNativeWebView) {
      EventEmitter.addLogs('syncLogs', logLevel, logMessage);
    }
  };

  setSyncStatusToConnected = () => {
    try {
      //set sync status to connected
      dispatchUpdateSyncStatus(SyncStatus.SOCKET);
      dispatchRethinkDBLogs(LogLevelEnum.INFO, `CApi WebSocket is connected`);
      if (window.ReactNativeWebView) {
        //emit an event to RN when CApi is connected
        EventEmitter.syncStatus(SyncStatus.SOCKET);
        //emit an event for logs
        this.emitSyncLogs(LogLevelEnum.INFO, `CApi WebSocket is connected`);
      }
    } catch (e) {
      console.error('setSyncStatusToConnected error', e);
    }
  };

  setSyncStatusToDisconnected = () => {
    try {
      //set sync status to connected
      dispatchUpdateSyncStatus(SyncStatus.DISCONNECTED);
      dispatchRethinkDBLogs(LogLevelEnum.ERROR, `CApi WebSocket Connection lost. Attempting to reconnect`);
      if (window.ReactNativeWebView) {
        //emit an event to RN when CApi is connected
        EventEmitter.syncStatus(SyncStatus.DISCONNECTED);
        //emit an event for logs
        this.emitSyncLogs(LogLevelEnum.ERROR, `CApi WebSocket Connection lost. Attempting to reconnect`);
      }
    } catch (e) {
      console.error('setSyncStatusToDisconnected error', e);
    }
  };

  sendHealthInfo = () => {
    try {
      this.healthInfo.currentDate = new Date();

      if (this.healthInfo.lastConnectDate && this.healthInfo.connected) {
        this.healthInfo.currentConnectionDurationInMilliseconds = this.healthInfo.currentDate - this.healthInfo.lastConnectDate;
      }

      API.logToApiLog(this.healthInfo, '/capi/client/healthInfo')
        .then((response) => {
          FireboardUtils.addNetworkLogs(response);
          console.log('successfully logged capi client health info', this.healthInfo);
        })
        .catch((error) => {
          FireboardUtils.addNetworkLogs(error.response);
          console.error('error logging capi client health info', this.healthInfo, error);
        });
    } catch (e) {
      console.error('error sending capi client health info', this.healthInfo, e);
    }
  };
}

export default CApiWebSocketService;
